import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const sections = [
  {
    title: '1. Introduction',
    content: `Filter Hotels ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our B2B hotel aggregation platform. By using our Service, you consent to the data practices described in this policy.`,
  },
  {
    title: '2. Information We Collect',
    content: `We collect information you provide directly to us, including: personal identification information (name, email address, phone number), business information (business name, GST number, PAN number, Udyam Aadhar Registration Number), location information (city, state, country, ZIP code), account credentials, and booking and transaction data. We also automatically collect certain technical information when you use our platform, including IP addresses, browser type, device information, and usage patterns.`,
  },
  {
    title: '3. How We Use Your Information',
    content: `We use the information we collect to: provide, maintain, and improve our services; process bookings and transactions; send transactional emails and notifications; verify your identity and prevent fraud; comply with legal obligations; respond to your inquiries and support requests; analyze usage patterns to improve our platform; and communicate updates, promotions, and relevant information about our services.`,
  },
  {
    title: '4. Information Sharing & Disclosure',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information with: hotel vendors to process bookings; payment processors to handle transactions; service providers who assist in our operations; law enforcement or regulatory authorities when required by law; and business partners with your consent. All third parties are required to maintain the confidentiality of your information.`,
  },
  {
    title: '5. Data Storage & Security',
    content: `Your information is stored on secure servers and protected using industry-standard security measures, including encryption, secure socket layer (SSL) technology, and access controls. We use Redis for session management and MongoDB for data storage, both configured with appropriate security settings. While we implement robust security measures, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.`,
  },
  {
    title: '6. Session & Cookie Data',
    content: `We use sessions and cookies to maintain your login state and improve your experience on our platform. Session data is stored securely and automatically cleared upon logout. We use JWT (JSON Web Tokens) for authentication, which are stored locally in your browser. You can configure your browser to refuse cookies, but this may affect the functionality of our platform.`,
  },
  {
    title: '7. Data Retention',
    content: `We retain your personal information for as long as your account is active or as needed to provide services. Booking and transaction records are retained for a minimum of 7 years for legal and accounting purposes. You may request deletion of your account and associated data, subject to our legal obligations to retain certain records.`,
  },
  {
    title: '8. Your Rights',
    content: `You have the right to: access the personal information we hold about you; correct inaccurate or incomplete information; request deletion of your personal information (subject to legal requirements); object to or restrict the processing of your information; and receive a copy of your data in a portable format. To exercise these rights, please contact us at filterhotels@gmail.com.`,
  },
  {
    title: '9. Third-Party Links',
    content: `Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit. Our Privacy Policy applies only to information collected through our platform.`,
  },
  {
    title: '10. Children\'s Privacy',
    content: `Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information promptly.`,
  },
  {
    title: '11. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on our platform and sending an email notification to registered users. Your continued use of the Service after such changes constitutes your acceptance of the updated policy.`,
  },
  {
    title: '12. Contact Us',
    content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at: Email: filterhotels@gmail.com | Phone: +91 7020886313 | Business Hours: Monday–Friday, 9:00 AM–6:00 PM IST.`,
  },
];

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy – Filter Hotels';
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-900 to-blue-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-300 hover:text-white transition-colors mb-8 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm">FH</span>
            </div>
            <span className="text-white/80 font-medium">Filter Hotels</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-blue-200 text-lg">
            Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12"
        >
          <p className="text-gray-600 leading-relaxed mb-10 text-base border-l-4 border-indigo-500 pl-4 bg-indigo-50 py-3 pr-4 rounded-r-lg">
            Your privacy is important to us. This policy describes how Filter Hotels collects, uses, and protects your personal information.
          </p>

          <div className="space-y-10">
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h2>
                <p className="text-gray-600 leading-relaxed">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
